import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

/**
 * SPÄŤ O JEDNU OBRAZOVKU – tam, kde človek bol (.498).
 *
 * Oto: „Dušana stále vracalo naspäť na hlavnú stranu Lead managementu. On sa
 * chce vždy vedieť vrátiť len o jedno."
 *
 * Tlačidlo Späť síce vracalo o jednu adresu, ale zoznam sa pri návrate
 * poskladal odznova: hľadaný text, filtre, radenie, koľko bolo načítané
 * a kde bol posunutý – všetko preč. Človek, ktorý si vyhľadal firmu,
 * otvoril ju a dal Späť, skončil na začiatku celého zoznamu.
 *
 * ── Ako ─────────────────────────────────────────────────────────────────
 * Každý krok v histórii prehliadača má svoj kľúč (`location.key`). Stav
 * zoznamu sa ukladá **pod ten kľúč** do `sessionStorage` (len táto karta).
 *
 *  • Späť / Dopredu → ten istý kľúč → vráti sa presne to, čo tam bolo.
 *  • Nový príchod cez menu → nový kľúč → čistý zoznam, ako doteraz.
 *
 * Posun obrazovky sa pamätá rovnako, pre všetky stránky naraz.
 */

const kluc = (k, nazov) => `spat:${k}:${nazov}`;
const citaj = (k) => { try { const v = sessionStorage.getItem(k); return v === null ? undefined : JSON.parse(v); } catch { return undefined; } };
const zapis = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* plná pamäť – nevadí */ } };

/** Ako `useState`, ale hodnota patrí tomuto kroku v histórii. */
export function useStavKroku(nazov, pociatocny) {
  const { key } = useLocation();
  const [v, setV] = useState(() => {
    const ulozene = citaj(kluc(key, nazov));
    return ulozene !== undefined ? ulozene : (typeof pociatocny === 'function' ? pociatocny() : pociatocny);
  });
  useEffect(() => { zapis(kluc(key, nazov), v); }, [key, nazov, v]);
  return [v, setV];
}

/**
 * Posun obrazovky pri Späť. Zapína sa raz v aplikácii.
 *
 * Zoznam sa po návrate načítava zo servera, takže na správne miesto sa dá
 * posunúť až keď je stránka dosť vysoká – skúša sa preto chvíľu dookola.
 */
export function useObnovaPosunu() {
  const loc = useLocation();
  const typ = useNavigationType();
  const predosly = useRef(loc.key);
  const predoslaCesta = useRef(loc.pathname);
  const posun = useRef(0);

  useEffect(() => {
    try { window.history.scrollRestoration = 'manual'; } catch { /* staršie prehliadače */ }
    const naPosun = () => { posun.current = window.scrollY; };
    window.addEventListener('scroll', naPosun, { passive: true });
    return () => window.removeEventListener('scroll', naPosun);
  }, []);

  useEffect(() => {
    /* Odchádzajúca obrazovka: zapamätať, kde bola (posledný známy posun –
     * nová stránka ho ešte nestihla zmeniť). */
    if (predosly.current !== loc.key) {
      zapis(kluc(predosly.current, '_posun'), posun.current);
      predosly.current = loc.key;
    }
    const inaStranka = predoslaCesta.current !== loc.pathname;
    predoslaCesta.current = loc.pathname;
    /* Nová obrazovka začína hore; zmena filtra v adrese na tej istej nie. */
    if (typ !== 'POP') { if (inaStranka) window.scrollTo(0, 0); return undefined; }
    const ciel = citaj(kluc(loc.key, '_posun'));
    if (!ciel) return undefined;
    let pokusov = 0;
    let zrusene = false;
    const skus = () => {
      if (zrusene) return;
      const da = document.documentElement.scrollHeight - window.innerHeight >= ciel - 2;
      if (da || pokusov > 60) { window.scrollTo(0, ciel); return; }
      pokusov++;
      setTimeout(skus, 50);
    };
    skus();
    /* Kto sa medzitým pohne sám, tomu to neskáče pod rukami. */
    const stop = () => { zrusene = true; };
    window.addEventListener('wheel', stop, { once: true, passive: true });
    window.addEventListener('touchmove', stop, { once: true, passive: true });
    return () => { zrusene = true; window.removeEventListener('wheel', stop); window.removeEventListener('touchmove', stop); };
  }, [loc.key, typ]);
}

/** „← Späť" na stránke: o jeden krok v histórii, keď sa dá; inak na `nahradna`. */
export function useSpat(nahradna) {
  const nav = useNavigate();
  const { key } = useLocation();
  return () => {
    const idx = window.history.state?.idx;
    if (key !== 'default' && typeof idx === 'number' && idx > 0) nav(-1);
    else nav(nahradna);
  };
}
